/**
 * User preferences sync — mirrors iOS UserPreferencesSyncService /
 * GET|PUT /users/me/preferences.
 *
 * This is NOT Device Sync and NOT CRM Client trip.preferences.
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';
import { detectBrowserTimeZone } from '../utils/timeZoneOptions';

export interface TripTaskTemplateItem {
  title: string;
  sortOrder: number;
}

export interface AppUserSettings {
  hidePastTrips: boolean;
  hidePastCities: boolean;
  hidePastDays: boolean;
  preferredMapApp: string;
  userNationality: string | null;
  homeTimeZone: string;
  expandAllItemsByDefault?: boolean | null;
  collapsePastItems: boolean;
  hideCompletedOptions: boolean;
  showPlanIcons?: boolean | null;
  communityFeaturesEnabled: boolean;
  hiddenFeaturesVisible: boolean;
  reservationWarningLevel: string;
  cruiseMealPlanningOption: string;
}

export interface TravelerPreferenceProfile {
  id: string;
  preferredAccommodationType: string | null;
  averageHotelBudget: number | null;
  preferredActivities: string[];
  dietaryPreferences: string[];
  travelPace: string | null;
  typicalStayDuration: number | null;
  preferredTimeSlots: string[];
  additionalPreferences: string | null;
  extractedPatterns: string[];
  lastUpdated: string;
  autoAnalyzeEnabled: boolean;
  cuisinePreferences: Record<string, string>;
  travelStyleTags?: string[] | null;
  sightseeingStyle?: string | null;
  defaultTripTaskTemplate?: TripTaskTemplateItem[] | null;
}

export interface UserPreferencesPayload {
  appUserSettings: AppUserSettings;
  travelerPreferenceProfile: TravelerPreferenceProfile;
}

const STORAGE_KEY = 'wt_user_preferences';
const PUT_DEBOUNCE_MS = 1500;

let putTimer: ReturnType<typeof setTimeout> | null = null;
let pendingPutPayload: UserPreferencesPayload | null = null;

export function createEmptyAppUserSettings(): AppUserSettings {
  return {
    hidePastTrips: false,
    hidePastCities: false,
    hidePastDays: false,
    preferredMapApp: 'apple',
    userNationality: null,
    homeTimeZone: detectBrowserTimeZone(),
    expandAllItemsByDefault: true,
    collapsePastItems: false,
    hideCompletedOptions: false,
    showPlanIcons: true,
    communityFeaturesEnabled: false,
    hiddenFeaturesVisible: false,
    reservationWarningLevel: 'recommended',
    cruiseMealPlanningOption: 'oneMeal',
  };
}

/** Wire body: OpenAPI TravelerPreferenceProfile rejects unknown keys (e.g. travelStyleTags). */
function travelerProfileForPut(
  profile: TravelerPreferenceProfile
): Omit<TravelerPreferenceProfile, 'travelStyleTags'> {
  const { travelStyleTags: _omitTravelStyleTags, ...rest } = profile;
  return rest;
}

export function createEmptyTravelerPreferenceProfile(): TravelerPreferenceProfile {
  return {
    id: '',
    preferredAccommodationType: null,
    averageHotelBudget: null,
    preferredActivities: [],
    dietaryPreferences: [],
    travelPace: null,
    typicalStayDuration: null,
    preferredTimeSlots: [],
    additionalPreferences: null,
    extractedPatterns: [],
    lastUpdated: new Date().toISOString(),
    autoAnalyzeEnabled: false,
    cuisinePreferences: {},
    travelStyleTags: [],
    sightseeingStyle: null,
    defaultTripTaskTemplate: [],
  };
}

export function createEmptyUserPreferences(): UserPreferencesPayload {
  return {
    appUserSettings: createEmptyAppUserSettings(),
    travelerPreferenceProfile: createEmptyTravelerPreferenceProfile(),
  };
}

export function loadLocalUserPreferences(): UserPreferencesPayload {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createEmptyUserPreferences();
    const parsed = JSON.parse(raw) as UserPreferencesPayload;
    return {
      appUserSettings: {
        ...createEmptyAppUserSettings(),
        ...(parsed.appUserSettings ?? {}),
      },
      travelerPreferenceProfile: {
        ...createEmptyTravelerPreferenceProfile(),
        ...(parsed.travelerPreferenceProfile ?? {}),
      },
    };
  } catch {
    return createEmptyUserPreferences();
  }
}

export function saveLocalUserPreferences(payload: UserPreferencesPayload): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export async function getUserPreferences(): Promise<UserPreferencesPayload> {
  const headers = await buildAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/users/me/preferences`, {
    method: 'GET',
    headers,
  });
  if (response.status === 404) {
    return createEmptyUserPreferences();
  }
  if (!response.ok) {
    throw new Error(`getUserPreferences failed: ${response.status}`);
  }
  const json = (await response.json()) as UserPreferencesPayload;
  const remoteTraveler = json.travelerPreferenceProfile ?? createEmptyTravelerPreferenceProfile();
  const remoteId = (remoteTraveler.id ?? '').trim();
  const localId = (loadLocalUserPreferences().travelerPreferenceProfile.id ?? '').trim();
  const normalized: UserPreferencesPayload = {
    appUserSettings: {
      ...createEmptyAppUserSettings(),
      ...(json.appUserSettings ?? {}),
    },
    travelerPreferenceProfile: {
      ...createEmptyTravelerPreferenceProfile(),
      ...remoteTraveler,
      id: remoteId || localId,
    },
  };
  saveLocalUserPreferences(normalized);
  return normalized;
}

export async function putUserPreferences(
  payload: UserPreferencesPayload
): Promise<UserPreferencesPayload> {
  const headers = await buildAuthHeaders(true);
  const response = await fetch(`${API_BASE_URL}/users/me/preferences`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      appUserSettings: payload.appUserSettings,
      travelerPreferenceProfile: travelerProfileForPut(payload.travelerPreferenceProfile),
    }),
  });
  if (!response.ok) {
    throw new Error(`putUserPreferences failed: ${response.status}`);
  }
  const json = (await response.json()) as UserPreferencesPayload;
  const normalized: UserPreferencesPayload = {
    appUserSettings: {
      ...createEmptyAppUserSettings(),
      ...(json.appUserSettings ?? {}),
    },
    travelerPreferenceProfile: {
      ...createEmptyTravelerPreferenceProfile(),
      ...(json.travelerPreferenceProfile ?? {}),
    },
  };
  saveLocalUserPreferences(normalized);
  return normalized;
}

/** Normalize / validate payload shape (used by tests + local edits). */
export function normalizeUserPreferencesPayload(
  raw: unknown
): UserPreferencesPayload {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const app = (o.appUserSettings && typeof o.appUserSettings === 'object'
    ? o.appUserSettings
    : {}) as Partial<AppUserSettings>;
  const traveler = (o.travelerPreferenceProfile &&
  typeof o.travelerPreferenceProfile === 'object'
    ? o.travelerPreferenceProfile
    : {}) as Partial<TravelerPreferenceProfile>;
  return {
    appUserSettings: { ...createEmptyAppUserSettings(), ...app },
    travelerPreferenceProfile: {
      ...createEmptyTravelerPreferenceProfile(),
      ...traveler,
    },
  };
}

/** Local-first patch of appUserSettings; schedules a debounced PUT. */
export function patchAppUserSettings(
  patch: Partial<AppUserSettings>
): UserPreferencesPayload {
  const current = loadLocalUserPreferences();
  const next: UserPreferencesPayload = {
    ...current,
    appUserSettings: {
      ...current.appUserSettings,
      ...patch,
    },
  };
  saveLocalUserPreferences(next);
  schedulePreferencesPut(next);
  return next;
}

/** Local-first patch of travelerPreferenceProfile; schedules a debounced PUT. */
export function patchTravelerPreferenceProfile(
  patch: Partial<TravelerPreferenceProfile>
): UserPreferencesPayload {
  const current = loadLocalUserPreferences();
  const next: UserPreferencesPayload = {
    ...current,
    travelerPreferenceProfile: {
      ...current.travelerPreferenceProfile,
      ...patch,
      lastUpdated: new Date().toISOString(),
    },
  };
  saveLocalUserPreferences(next);
  schedulePreferencesPut(next);
  return next;
}

/** Debounced PUT (~1.5s), mirroring iOS UserPreferencesSyncService. */
export function schedulePreferencesPut(
  payload: UserPreferencesPayload,
  delayMs: number = PUT_DEBOUNCE_MS
): void {
  pendingPutPayload = payload;
  if (putTimer) clearTimeout(putTimer);
  putTimer = setTimeout(() => {
    putTimer = null;
    const toSend = pendingPutPayload;
    pendingPutPayload = null;
    if (!toSend) return;
    void putUserPreferences(toSend).catch((err) => {
      console.warn('userPreferencesSyncService: put failed', err);
    });
  }, delayMs);
}

/** Cancel a queued debounced PUT without sending it. */
export function cancelScheduledPreferencesPut(): void {
  if (putTimer) {
    clearTimeout(putTimer);
    putTimer = null;
  }
  pendingPutPayload = null;
}

/** Flush any pending debounced PUT immediately (e.g. leaving Settings). */
export async function flushPreferencesPut(): Promise<void> {
  if (putTimer) {
    clearTimeout(putTimer);
    putTimer = null;
  }
  const toSend = pendingPutPayload ?? loadLocalUserPreferences();
  pendingPutPayload = null;
  await putUserPreferences(toSend);
}

/** True when a debounced preferences PUT is waiting to send. */
export function hasPendingPreferencesPut(): boolean {
  return putTimer != null || pendingPutPayload != null;
}

/** Test helper — clear debounce timer between tests. */
export function resetPreferencesPutSchedulerForTesting(): void {
  cancelScheduledPreferencesPut();
}
